import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { api, downloadOrderPdf, downloadOrdersExport, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Wallet, FileSpreadsheet, ShoppingCart, PieChart as PieIcon, Loader2, Truck, CalendarRange, Mail, History } from "lucide-react";

const COLORS = ["#0ea5e9", "#a855f7", "#f59e0b", "#10b981", "#f43f5e", "#6366f1", "#14b8a6", "#f97316", "#ec4899", "#84cc16", "#eab308", "#06b6d4"];
const eur = (n) => Number(n || 0).toLocaleString("fr-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const fmt = (n) => Number(n || 0).toLocaleString("fr-BE", { maximumFractionDigits: 2 });
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

const PERIODS = [
  { value: "1", label: "1 semaine", factor: 1 },
  { value: "2", label: "2 semaines", factor: 2 },
  { value: "3", label: "3 semaines", factor: 3 },
  { value: "4", label: "1 mois (4 sem.)", factor: 4 },
];

const SUP_EMPTY = { supplier_name: "", supplier_address: "", supplier_phone: "", supplier_fax: "", supplier_email: "" };

export function StatsBudget({ orderLines, totalCost, weekLabel }) {
  const { t } = useTranslation();
  const [budget, setBudget] = useState(0);
  const [supplier, setSupplier] = useState(SUP_EMPTY);
  const [period, setPeriod] = useState("1");
  const [stats, setStats] = useState({ items: [], weeks: 0 });
  const [orders, setOrders] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const timer = useRef(null);
  const loaded = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, q] = await Promise.all([api.get("/inc/settings"), api.get("/inc/stats/quarter")]);
        setBudget(s.data.budget || 0);
        setSupplier({ supplier_name: s.data.supplier_name || "", supplier_address: s.data.supplier_address || "", supplier_phone: s.data.supplier_phone || "", supplier_fax: s.data.supplier_fax || "", supplier_email: s.data.supplier_email || "" });
        setStats(q.data || { items: [], weeks: 0 });
      } catch { /* silent */ }
      loadOrders();
      setTimeout(() => { loaded.current = true; }, 50);
    })();
  }, []);

  useEffect(() => {
    if (!loaded.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { api.put("/inc/settings", { budget: Number(budget) || 0, ...supplier }).catch(() => {}); }, 700);
    return () => clearTimeout(timer.current);
  }, [budget, supplier]);

  const factor = PERIODS.find((p) => p.value === period)?.factor || 1;
  const periodLabel = t(`inc.periods.${period}`, { defaultValue: PERIODS.find((p) => p.value === period)?.label || "1 semaine" });
  const lines = orderLines.map((l) => ({ ...l, packages: l.packages * factor, cartons: l.cartons * factor, cost: l.cost * factor }));
  const periodTotal = totalCost * factor;
  const rest = (Number(budget) || 0) - periodTotal;
  const over = rest < 0;

  const generate = async () => {
    if (!lines.length) return toast.error(t("inc.noOrder"));
    setGenerating(true);
    try {
      await downloadOrderPdf({ week_label: weekLabel || "", period_label: periodLabel, lines, total_cost: periodTotal, budget: Number(budget) || 0, supplier });
      toast.success(t("inc.orderGenerated"));
      loadOrders();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Échec de la génération"); }
    finally { setGenerating(false); }
  };

  const sendByEmail = async () => {
    if (!lines.length) return toast.error(t("inc.noOrder"));
    if (!supplier.supplier_email?.trim()) return toast.error(t("inc.supplierEmailRequired"));
    setEmailing(true);
    try {
      const res = await api.post(`/inc/order-email?recipient_email=${encodeURIComponent(supplier.supplier_email.trim())}`,
        { week_label: weekLabel || "", period_label: periodLabel, lines, total_cost: periodTotal, budget: Number(budget) || 0, supplier });
      toast.success(t("inc.orderEmailSent", { email: res.data.recipient }));
      loadOrders();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("inc.orderEmailFailed")); }
    finally { setEmailing(false); }
  };

  const loadOrders = async () => {
    try { const r = await api.get("/inc/orders"); setOrders(r.data.orders || []); } catch { /* silent */ }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-7">
      {/* Budget + bon de commande */}
      <div className="glass rounded-2xl card-3d p-5" data-testid="inc-budget-order" data-tour="inc-budget">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-emerald-300" /> {t("inc.budgetOrderTitle")}</h2>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={generate} disabled={generating} className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg shadow-emerald-500/20" data-testid="inc-generate-order">
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />} {t("inc.generateOrder")}
            </Button>
            <Button onClick={sendByEmail} disabled={emailing} variant="outline" className="border-sky-400/40 text-sky-200 hover:bg-sky-500/10" data-testid="inc-send-order-email">
              {emailing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />} {t("inc.sendOrder")}
            </Button>
          </div>
        </div>

        {/* Période + budget */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="glass rounded-xl px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-1.5"><CalendarRange className="h-3.5 w-3.5" /> {t("inc.period")}</div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-9" data-testid="inc-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{t(`inc.periods.${p.value}`, { defaultValue: p.label })}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="glass rounded-xl px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-1.5"><Wallet className="h-3.5 w-3.5" /> {t("inc.budget")}</div>
            <Input type="number" min="0" step="0.01" value={budget || ""} placeholder="0,00" onChange={(e) => setBudget(e.target.value === "" ? 0 : Number(e.target.value))} className="h-9" data-testid="inc-budget-input" />
          </div>
        </div>

        {/* Fournisseur */}
        <div className="rounded-xl border border-white/10 p-3 mb-4">
          <div className="text-xs uppercase tracking-wider text-sky-200 font-semibold flex items-center gap-1.5 mb-2"><Truck className="h-3.5 w-3.5" /> {t("inc.supplier")}</div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={supplier.supplier_name} onChange={(e) => setSupplier({ ...supplier, supplier_name: e.target.value })} placeholder={t("inc.supName")} data-testid="inc-supplier-name" className="h-9" />
            <Input value={supplier.supplier_address} onChange={(e) => setSupplier({ ...supplier, supplier_address: e.target.value })} placeholder={t("inc.supAddress")} data-testid="inc-supplier-address" className="h-9" />
            <Input value={supplier.supplier_phone} onChange={(e) => setSupplier({ ...supplier, supplier_phone: e.target.value })} placeholder={t("inc.supPhone")} data-testid="inc-supplier-phone" className="h-9" />
            <Input value={supplier.supplier_fax} onChange={(e) => setSupplier({ ...supplier, supplier_fax: e.target.value })} placeholder={t("inc.supFax")} data-testid="inc-supplier-fax" className="h-9" />
            <Input type="email" value={supplier.supplier_email} onChange={(e) => setSupplier({ ...supplier, supplier_email: e.target.value })} placeholder={t("inc.supplierEmail")} data-testid="inc-supplier-email" className="h-9 col-span-2" />
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#161427] text-[11px] uppercase text-muted-foreground">
              <tr><th className="px-2 py-2 text-left">Réf.</th><th className="px-3 py-2 text-left">Produit</th><th className="px-2 py-2 text-right">Embal.</th><th className="px-2 py-2 text-right">Cartons</th><th className="px-2 py-2 text-right">Montant</th></tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-t border-white/5" data-testid={`inc-order-line-${i}`}>
                  <td className="px-2 py-1.5 font-mono text-xs text-muted-foreground">{l.reference || "—"}</td>
                  <td className="px-3 py-1.5"><span className={`inline-block h-2 w-2 rounded-full mr-2 ${l.category === "JOUR" ? "bg-amber-400" : "bg-indigo-400"}`} />{l.name}</td>
                  <td className="px-2 py-1.5 text-right">{fmt(l.packages)}</td>
                  <td className="px-2 py-1.5 text-right font-semibold">{l.carton_size ? fmt(l.cartons) : "—"}</td>
                  <td className="px-2 py-1.5 text-right">{l.price_per_carton ? eur(l.cost) : "—"}</td>
                </tr>
              ))}
              {!lines.length && <tr><td colSpan={5} className="px-3 py-5 text-center text-muted-foreground text-sm">{t("inc.noQty")}</td></tr>}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-emerald-400/30 bg-emerald-500/10 font-bold">
                <td className="px-3 py-2 uppercase text-[11px]" colSpan={4}>{t("inc.orderTotal")} · {periodLabel}</td>
                <td className="px-2 py-2 text-right text-emerald-200" data-testid="inc-order-total">{eur(periodTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {Number(budget) > 0 && (
          <div className={`mt-3 rounded-xl px-4 py-3 text-sm font-semibold flex items-center justify-between ${over ? "bg-red-500/10 text-red-300 border border-red-400/30" : "bg-emerald-500/10 text-emerald-300 border border-emerald-400/30"}`} data-testid="inc-budget-rest">
            <span>{over ? t("inc.overBudget") : t("inc.restAfter")}</span>
            <span className="text-base">{eur(rest)}</span>
          </div>
        )}
      </div>

      {/* Statistiques trimestrielles */}
      <div className="glass rounded-2xl card-3d p-5" data-testid="inc-stats">
        <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-1"><PieIcon className="h-5 w-5 text-purple-300" /> {t("inc.statsTitle")}</h2>
        <p className="text-sm text-muted-foreground mb-4">{t("inc.statsSub", { n: stats.weeks })}</p>
        {stats.items.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} style={{ width: "100%", minWidth: 0, height: 230 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.items} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} stroke="none">
                    {stats.items.map((it, i) => <Cell key={it.id} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#161427", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} formatter={(v) => [fmt(v), t("inc.units")]} />
                </PieChart>
              </ResponsiveContainer>
            </motion.div>
            <div className="space-y-1.5 max-h-[230px] overflow-y-auto pr-1">
              {stats.items.map((it, i) => (
                <div key={it.id} className="flex items-center gap-2 text-sm" data-testid={`inc-stat-${it.id}`}>
                  <span className="h-3 w-3 rounded-sm shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="flex-1 truncate">{it.name}</span>
                  <span className="font-semibold text-muted-foreground">{fmt(it.value)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="py-10 text-center text-muted-foreground text-sm">{t("inc.noStats")}</div>
        )}
      </div>

      {/* Historique des commandes */}
      <div className="glass rounded-2xl card-3d p-5 xl:col-span-2" data-testid="inc-orders-history">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><History className="h-5 w-5 text-sky-300" /> {t("inc.ordersHistoryTitle")}</h2>
          <Button size="sm" variant="outline" className="border-sky-400/40 text-sky-200 hover:bg-sky-500/10" onClick={() => downloadOrdersExport()} disabled={!orders.length} data-testid="inc-orders-export"><FileSpreadsheet className="h-4 w-4 mr-2" /> {t("inc.exportQuarter")}</Button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{t("inc.ordersHistorySub")}</p>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#161427] text-[11px] uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">{t("inc.ordDate")}</th>
                <th className="px-3 py-2 text-left">{t("inc.ordSupplier")}</th>
                <th className="px-3 py-2 text-left">{t("inc.ordPeriod")}</th>
                <th className="px-3 py-2 text-left">{t("inc.ordResponsible")}</th>
                <th className="px-2 py-2 text-center">{t("inc.ordChannel")}</th>
                <th className="px-3 py-2 text-right">{t("inc.ordTotal")}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`inc-order-row-${o.id}`}>
                  <td className="px-3 py-1.5 whitespace-nowrap text-muted-foreground">{fmtDate(o.created_at)}</td>
                  <td className="px-3 py-1.5 font-medium">{o.supplier_name}{o.recipient ? <span className="block text-[11px] text-muted-foreground">{o.recipient}</span> : null}</td>
                  <td className="px-3 py-1.5">{o.period_label || "—"}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">{o.responsible || "—"}</td>
                  <td className="px-2 py-1.5 text-center">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${o.channel === "email" ? "bg-sky-500/15 text-sky-200 border-sky-400/30" : "bg-white/5 text-muted-foreground border-white/10"}`}>
                      {o.channel === "email" ? <Mail className="h-3 w-3" /> : <FileSpreadsheet className="h-3 w-3" />} {o.channel === "email" ? t("inc.ordEmail") : "PDF"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-bold text-emerald-200">{eur(o.total_cost)}</td>
                </tr>
              ))}
              {!orders.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground text-sm">{t("inc.ordersEmpty")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
